// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

export interface ParsedRow {
  name: string
  dob: string
  appointmentDateTime: string
  provider: string
  phone: string
  email: string
}

export interface RowResult {
  rowNumber: number
  data: ParsedRow
  errors: string[]
}

export interface ParseResult {
  rows: RowResult[]
  truncated: boolean
}

const MAX_ROWS = 200

function splitCsvLine(line: string): string[] {
  const fields: string[] = []
  let current = ''
  let inQuotes = false
  for (const ch of line) {
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      fields.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  fields.push(current.trim())
  return fields
}

function validateRow(data: ParsedRow): string[] {
  const errors: string[] = []
  if (!data.name.trim()) {
    errors.push('Name is required')
  }
  if (!data.dob.trim()) {
    errors.push('Invalid or future date of birth')
  } else {
    const d = new Date(data.dob)
    if (isNaN(d.getTime()) || d > new Date()) {
      errors.push('Invalid or future date of birth')
    }
  }
  if (!data.appointmentDateTime.trim()) {
    errors.push('Invalid appointment date/time')
  } else if (isNaN(new Date(data.appointmentDateTime).getTime())) {
    errors.push('Invalid appointment date/time')
  }
  return errors
}

export function parseCsv(csvText: string): ParseResult {
  const lines = csvText.split(/\r?\n/).filter(l => l.trim().length > 0)
  if (lines.length < 2) return { rows: [], truncated: false }

  const headerLine = lines[0]!
  const headers = splitCsvLine(headerLine).map(h =>
    h.toLowerCase().replace(/\s+/g, '_'),
  )

  const idx = {
    name: headers.indexOf('name'),
    dob: headers.indexOf('dob'),
    appointmentDateTime: headers.indexOf('appointment_date_time'),
    provider: headers.indexOf('provider'),
    phone: headers.indexOf('phone'),
    email: headers.indexOf('email'),
  }

  const dataLines = lines.slice(1)
  const truncated = dataLines.length > MAX_ROWS
  const limited = dataLines.slice(0, MAX_ROWS)

  const rows: RowResult[] = limited.map((line, i) => {
    const fields = splitCsvLine(line)
    const get = (index: number): string =>
      index >= 0 ? (fields[index] ?? '') : ''

    const data: ParsedRow = {
      name: get(idx.name),
      dob: get(idx.dob),
      appointmentDateTime: get(idx.appointmentDateTime),
      provider: get(idx.provider) || 'No provider',
      phone: get(idx.phone),
      email: get(idx.email),
    }

    return { rowNumber: i + 2, data, errors: validateRow(data) }
  })

  return { rows, truncated }
}
